import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import "@/App.css";
import { ArrowRight, Sparkles, X, FileText, Loader2, Download, LogOut, Lock, AlertTriangle } from "lucide-react";
import BlueprintView, { SECTIONS } from "./components/BlueprintView";
import EvaluatorTool from "./components/EvaluatorTool";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios must send the session_token httpOnly cookie on every request.
axios.defaults.withCredentials = true;

const useActiveSection = (ids) => {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const handler = () => {
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top < 160) current = id;
      }
      setActive(current);
    };
    window.addEventListener("scroll", handler, { passive: true });
    handler();
    return () => window.removeEventListener("scroll", handler);
  }, [ids]);
  return active;
};

const TOC = ({ visible }) => {
  const active = useActiveSection(SECTIONS.map((s) => s.id));
  return (
    <nav
      data-testid="toc-nav"
      className={`hidden lg:block fixed top-32 left-8 xl:left-12 z-20 transition-opacity ${visible ? "opacity-100" : "opacity-0 pointer-events-none"}`}
    >
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] mb-3 pl-[18px]">
        Contents
      </div>
      <ul className="space-y-0 max-w-[180px]">
        {SECTIONS.map((s) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              data-testid={`toc-${s.id}`}
              className={`toc-link ${active === s.id ? "active" : ""}`}
            >
              <span className="mr-2">{s.num}</span>
              {s.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};
/* Editorial marquee banner — sits under the TopBar. Communicates the free-tier
   rate-limit reality without shouting. Dismissible; choice persists in localStorage. */
const MARQUEE_TEXT = "This is a free service · Our backend has daily rate limits · If you hit an error while generating, please retry in 24 hours";

const MarqueeBanner = () => {
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem("bp_marquee_v2_hidden") === "1"; } catch (e) { return false; }
  });
  if (hidden) return null;
  const dismiss = () => {
    try { localStorage.setItem("bp_marquee_v2_hidden", "1"); } catch (e) { /* ignore */ }
    setHidden(true);
  };
  // Repeat the message so the loop feels continuous regardless of viewport width.
  const track = Array.from({ length: 4 }).map((_, i) => (
    <span key={i} className="inline-flex items-center gap-3 mx-8 whitespace-nowrap">
      <AlertTriangle className="w-3.5 h-3.5 text-[#002FA7] shrink-0" />
      {MARQUEE_TEXT}
    </span>
  ));
  return (
    <div
      data-testid="marquee-banner"
      className="sticky top-14 z-20 bg-[#0A0A0A] text-[#F9F8F6] border-b border-[#0A0A0A] overflow-hidden"
    >
      <div className="flex items-center">
        <div className="marquee-viewport flex-1 min-w-0 overflow-hidden py-2.5">
          <div className="marquee-track flex font-mono text-[10px] tracking-[0.18em] uppercase">
            {track}{track}
          </div>
        </div>
        <button
          data-testid="marquee-dismiss"
          onClick={dismiss}
          aria-label="Dismiss banner"
          title="Dismiss"
          className="shrink-0 px-3 py-2.5 text-[#F9F8F6]/70 hover:text-[#F9F8F6] hover:bg-[#F9F8F6]/10 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};



const TopBar = ({ onGenerate, onDownload, canDownload, downloading, user, onSignIn, onSignOut }) => (
  <header className="sticky top-0 z-30 bg-[#F9F8F6]/85 backdrop-blur border-b border-[#0A0A0A]/12">
    <div className="max-w-7xl mx-auto px-6 sm:px-10 h-14 flex items-center justify-between">
      <a href="#top" className="flex items-baseline gap-2 group" data-testid="brand">
        <span className="font-serif text-2xl tracking-tight">BluePrint</span>
        <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7]">AI</span>
      </a>
      <div className="flex items-center gap-3">
        {canDownload && (
          <button
            data-testid="topbar-download"
            onClick={onDownload}
            disabled={downloading}
            className="border border-[#0A0A0A]/25 font-mono text-[11px] tracking-[0.18em] uppercase px-3 py-2.5 hover:border-[#002FA7] hover:text-[#002FA7] flex items-center gap-2 disabled:opacity-50"
          >
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {downloading ? "Preparing…" : "PDF"}
          </button>
        )}
        <button
          data-testid="topbar-generate"
          onClick={onGenerate}
          className="bg-[#002FA7] text-white font-mono text-[11px] tracking-[0.18em] uppercase px-4 py-2.5 hover:bg-[#0A0A0A] flex items-center gap-2"
        >
          <Sparkles className="w-3.5 h-3.5" />
          Generate
        </button>
        {user ? (
          <UserChip user={user} onSignOut={onSignOut} />
        ) : (
          <button
            data-testid="topbar-signin"
            onClick={onSignIn}
            className="border border-[#0A0A0A]/25 font-mono text-[11px] tracking-[0.18em] uppercase px-3 py-2.5 hover:border-[#002FA7] hover:text-[#002FA7] flex items-center gap-2"
            title="Sign in to download PDFs"
          >
            Sign in
          </button>
        )}
      </div>
    </div>
  </header>
);

const UserChip = ({ user, onSignOut }) => {
  const [open, setOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  useEffect(() => {
    // Check if the current user is also an admin so we can show the "Admin" entry.
    axios.get(`${API}/admin/me`).then(() => setIsAdmin(true)).catch(() => setIsAdmin(false));
  }, []);
  const initials = (user.name || user.email || "?").split(/\s+/).map(s => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="user-chip"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 border border-[#0A0A0A]/20 hover:border-[#002FA7] px-2 py-1.5"
      >
        {user.picture ? (
          <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
        ) : (
          <span className="w-7 h-7 rounded-full bg-[#002FA7] text-white font-mono text-[10px] flex items-center justify-center">{initials}</span>
        )}
        <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-[#0A0A0A] hidden sm:block max-w-[110px] truncate">
          {(user.name || user.email).split(" ")[0]}
        </span>
      </button>
      {open && (
        <div data-testid="user-menu" className="absolute right-0 mt-1 w-60 bg-[#F9F8F6] border border-[#0A0A0A]/15 shadow-md z-50">
          <div className="px-4 py-3 border-b border-[#0A0A0A]/10">
            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#5C5C5C]">Signed in</div>
            <div className="font-serif text-base text-[#0A0A0A] truncate">{user.name}</div>
            <div className="font-mono text-[10px] text-[#5C5C5C] truncate">{user.email}</div>
          </div>
          {isAdmin && (
            <a
              href="/admin"
              data-testid="user-menu-admin"
              className="block px-4 py-3 hover:bg-[#EFEFE8] font-mono text-[11px] tracking-[0.18em] uppercase text-[#002FA7] border-b border-[#0A0A0A]/10"
            >
              Admin Console →
            </a>
          )}
          <button
            data-testid="signout-button"
            onClick={() => { setOpen(false); onSignOut(); }}
            className="w-full text-left px-4 py-3 hover:bg-[#EFEFE8] font-mono text-[11px] tracking-[0.18em] uppercase text-[#0A0A0A] flex items-center gap-2"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
};

const SignInModal = ({ open, onClose, reason }) => {
  if (!open) return null;
  const handleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div
      data-testid="signin-modal"
      className="fixed inset-0 z-50 bg-[#0A0A0A]/55 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#F9F8F6] border border-[#0A0A0A]/20 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#0A0A0A]/12 px-7 py-4">
          <div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7]">Sign in</div>
            <div className="font-serif text-2xl">One click. No password.</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#EFEFE8]" data-testid="signin-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-7 space-y-5">
          <p className="text-sm text-[#0A0A0A]/70 leading-relaxed">
            {reason === "pdf"
              ? "Sign in with Google to download a corporate-ready PDF copy of this research memo."
              : "Sign in with Google. The memo generator and quick evaluator stay free without an account."}
          </p>
          <button
            onClick={handleSignIn}
            data-testid="google-signin-button"
            className="w-full bg-[#0A0A0A] text-[#F9F8F6] font-mono text-xs tracking-[0.22em] uppercase px-5 py-4 hover:bg-[#002FA7] flex items-center justify-center gap-3"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20.4H24v7.2h11.3c-1.6 4.7-6.1 8.1-11.3 8.1-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.1-5.1C33.6 6.1 29 4.4 24 4.4 13.2 4.4 4.4 13.2 4.4 24S13.2 43.6 24 43.6 43.6 34.8 43.6 24c0-1.2-.1-2.4-.3-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.5l5.9 4.3C13.8 15.1 18.5 12 24 12c3 0 5.8 1.1 7.9 3l5.1-5.1C33.6 6.1 29 4.4 24 4.4 16.5 4.4 10 8.6 6.3 14.5z"/>
              <path fill="#4CAF50" d="M24 43.6c4.9 0 9.4-1.7 12.9-4.6l-6-4.9c-1.9 1.3-4.3 2.1-6.9 2.1-5.2 0-9.6-3.3-11.2-7.9l-5.9 4.5C10 39.4 16.5 43.6 24 43.6z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20.4H24v7.2h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6 4.9C40.6 35.3 43.6 30 43.6 24c0-1.2-.1-2.4-.3-3.5z"/>
            </svg>
            Continue with Google
          </button>
          <p className="text-xs text-[#0A0A0A]/55 leading-relaxed">
            We only store your name, email and avatar to identify your account. No emails, no spam.
          </p>
        </div>
      </div>
    </div>
  );
};

const Hero = ({ onGenerate, onScrollToMemo }) => (
  <section id="top" className="border-b border-[#0A0A0A]/12">
    <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-20 sm:pb-28">
      <h1 className="font-serif font-light text-[44px] sm:text-7xl xl:text-[96px] leading-[0.95] tracking-tighter text-[#0A0A0A] max-w-5xl">
        A VC-grade <em className="not-italic text-[#002FA7]">research memo</em> for every startup idea — in 60&nbsp;seconds.
      </h1>
      <p className="mt-8 max-w-2xl text-base sm:text-lg text-[#0A0A0A]/70 leading-relaxed">
        Drop an idea. Get a brutally-honest 10-section blueprint: market sizing, competitors, AI moat, MVP scope, tech stack, database schema, GTM, financials, risk register and a 90-day execution plan. Written like a YC partner would, formatted like a McKinsey deliverable.
      </p>

      <div className="mt-12 flex flex-wrap items-center gap-4">
        <button
          data-testid="hero-generate"
          onClick={onGenerate}
          className="bg-[#0A0A0A] text-[#F9F8F6] font-mono text-xs tracking-[0.22em] uppercase px-6 py-4 flex items-center gap-3 hover:bg-[#002FA7]"
        >
          Generate New Blueprint <ArrowRight className="w-4 h-4" />
        </button>
        <button
          data-testid="hero-showcase"
          onClick={onScrollToMemo}
          className="border border-[#0A0A0A]/30 font-mono text-xs tracking-[0.22em] uppercase px-6 py-4 flex items-center gap-3 hover:border-[#002FA7] hover:text-[#002FA7]"
        >
          <FileText className="w-4 h-4" /> Read Featured Memo
        </button>
      </div>
    </div>
  </section>
);

const FeaturedBadge = () => (
  <div className="border-y border-[#0A0A0A]/12 bg-[#EFEFE8]">
    <div className="max-w-7xl mx-auto px-6 sm:px-10 py-4 flex items-center justify-between flex-wrap gap-3">
      <div className="flex items-center gap-3">
        <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7]">Featured Memo</span>
        <span className="font-serif text-base text-[#0A0A0A]/70">— AI-Powered Home Services Marketplace</span>
      </div>
      <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C]">
        Pre-loaded · Reading time ~12 min
      </span>
    </div>
  </div>
);

const GenerateModal = ({ open, onClose, onSubmit, loading, initialIdea = "" }) => {
  const [idea, setIdea] = useState(initialIdea);
  const [industry, setIndustry] = useState("");
  const [target, setTarget] = useState("");

  useEffect(() => {
    if (open) setIdea(initialIdea || "");
  }, [open, initialIdea]);

  if (!open) return null;

  return (
    <div
      data-testid="generate-modal"
      className="fixed inset-0 z-50 bg-[#0A0A0A]/55 flex items-start justify-center p-4 sm:p-10 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-[#F9F8F6] border border-[#0A0A0A]/20 w-full max-w-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#0A0A0A]/12 px-7 py-4">
          <div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7]">New Memo</div>
            <div className="font-serif text-2xl">Brief the AI strategist.</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#EFEFE8]" data-testid="modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!idea.trim() || loading) return;
            onSubmit({ idea: idea.trim(), industry: industry.trim() || undefined, target_market: target.trim() || undefined });
          }}
          className="p-7 space-y-6"
        >
          <div>
            <label className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] mb-2 block">
              The Idea*
            </label>
            <textarea
              data-testid="modal-idea"
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              required
              rows={5}
              placeholder="E.g., An AI-native bookkeeping copilot for solo founders that automates GST filings and category-based expense tagging from receipts and bank feeds."
              className="w-full bg-transparent border border-[#0A0A0A]/15 p-4 font-sans text-base focus:border-[#002FA7] focus:outline-none resize-none"
              maxLength={2000}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] mb-2 block">
                Industry (optional)
              </label>
              <input
                data-testid="modal-industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="Fintech, Proptech, Health…"
                className="w-full bg-transparent border border-[#0A0A0A]/15 p-3 font-sans text-sm focus:border-[#002FA7] focus:outline-none"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] mb-2 block">
                Target Market (optional)
              </label>
              <input
                data-testid="modal-target"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                placeholder="India SMBs, US Gen-Z, …"
                className="w-full bg-transparent border border-[#0A0A0A]/15 p-3 font-sans text-sm focus:border-[#002FA7] focus:outline-none"
              />
            </div>
          </div>

          <div className="pt-4 border-t border-[#0A0A0A]/12 flex items-center justify-between flex-wrap gap-3">
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C]">
              ~60–120 seconds · 4 parallel Claude calls draft a full 10-section memo.
            </span>
            <button
              type="submit"
              disabled={loading || !idea.trim()}
              data-testid="modal-submit"
              className="bg-[#002FA7] text-white font-mono text-[11px] tracking-[0.22em] uppercase px-5 py-3 disabled:opacity-50 hover:bg-[#0A0A0A] flex items-center gap-2"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Drafting…</> : <>Draft Memo <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const LoadingMemo = () => (
  <div className="space-y-6">
    <div className="shimmer h-20 w-3/4" />
    <div className="shimmer h-6 w-1/2" />
    <div className="grid grid-cols-3 gap-4">
      <div className="shimmer h-32" />
      <div className="shimmer h-32" />
      <div className="shimmer h-32" />
    </div>
    <div className="shimmer h-48 w-full" />
    <div className="grid grid-cols-2 gap-4">
      <div className="shimmer h-40" />
      <div className="shimmer h-40" />
    </div>
    <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#002FA7] text-center pt-6">
      Drafting memo · this typically takes 60–120 seconds…
    </div>
  </div>
);

const Footer = () => (
  <footer className="border-t border-[#0A0A0A]/12 mt-32">
    <div className="max-w-7xl mx-auto px-6 sm:px-10 py-12">
      <div className="grid grid-cols-12 gap-6 mb-8">
        <div className="col-span-12 md:col-span-5">
          <div className="font-serif text-3xl tracking-tight">
            BluePrint<span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7] ml-1.5">AI</span>
          </div>
          <p className="text-sm text-[#0A0A0A]/65 mt-3 max-w-md leading-relaxed">
            A research-memo generator for founders, VCs and operators. Brutally honest, structured like a McKinsey deliverable, ready in 60 seconds.
          </p>
        </div>

        <div className="col-span-6 md:col-span-2">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] mb-3">Product</div>
          <ul className="space-y-2 text-sm text-[#0A0A0A]/80">
            <li><a href="#evaluator" className="hover:text-[#002FA7]">Quick Evaluator</a></li>
            <li><a href="#top" className="hover:text-[#002FA7]">Generate Memo</a></li>
            <li><a href="#validation" className="hover:text-[#002FA7]">Featured Memo</a></li>
          </ul>
        </div>

        <div className="col-span-6 md:col-span-2" data-testid="footer-contact">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] mb-3">Contact</div>
          <a
            href="mailto:contact@easyblueprintgen.com"
            className="font-mono text-sm text-[#002FA7] hover:text-[#0A0A0A] break-all"
          >
            contact@easyblueprintgen.com
          </a>
          <p className="text-xs text-[#0A0A0A]/60 mt-2 leading-relaxed">
            Press, partnerships, feedback. We read every email.
          </p>
        </div>

        <div className="col-span-12 md:col-span-3">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] mb-3">Disclaimer</div>
          <p className="text-xs text-[#0A0A0A]/60 leading-relaxed">
            All output is AI-generated and provided for research and ideation purposes only. Not legal, tax, financial or investment advice. Validate independently before relying on any recommendation.
          </p>
        </div>
      </div>

      <div className="border-t border-[#0A0A0A]/12 pt-6 flex flex-wrap items-baseline justify-between gap-3">
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C]">
          © 2026 BluePrintAI · Crafted with editorial precision.
        </div>
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C]">
          Built for founders &amp; the VCs who back them.
        </div>
      </div>
    </div>
  </footer>
);

function App() {
  const [showcase, setShowcase] = useState(null);
  const [current, setCurrent] = useState(null); // currently-displayed blueprint
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIdea, setModalIdea] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const memoRef = useRef(null);
  const [inMemo, setInMemo] = useState(false);

  // ---- Auth state ----
  const [user, setUser] = useState(null);
  const [signInOpen, setSignInOpen] = useState(false);
  const [signInReason, setSignInReason] = useState(null); // 'pdf' | null
  const sessionExchangedRef = useRef(false);

  // Synchronously process the URL fragment if it carries a session_id from Google redirect.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  useEffect(() => {
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (m && !sessionExchangedRef.current) {
      sessionExchangedRef.current = true;
      const sid = decodeURIComponent(m[1]);
      // Strip the fragment immediately so it's never reused.
      window.history.replaceState({}, "", window.location.pathname + window.location.search);
      axios.post(`${API}/auth/session`, { session_id: sid })
        .then((r) => setUser(r.data?.user || null))
        .catch((e) => {
          console.error("Session exchange failed", e);
          setError("Sign-in failed. Please try again.");
        });
      return;
    }
    // No session_id in hash → check existing cookie session.
    axios.get(`${API}/auth/me`)
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  const handleSignIn = (reason = null) => {
    setSignInReason(reason);
    setSignInOpen(true);
  };
  const handleSignOut = async () => {
    try { await axios.post(`${API}/auth/logout`, {}); } catch (e) { /* ignore */ }
    setUser(null);
  };

  // Track whether the user has scrolled past the memo's top
  useEffect(() => {
    const handler = () => {
      const top = memoRef.current?.getBoundingClientRect().top ?? Infinity;
      setInMemo(top < 120);
    };
    window.addEventListener("scroll", handler, { passive: true });
    handler();
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const handleDownload = async () => {
    if (!current?.id || downloadingPdf) return;
    if (!user) {
      handleSignIn("pdf");
      return;
    }
    setDownloadingPdf(true);
    setError(null);
    try {
      const url = `${API}/pdf/download/${encodeURIComponent(current.id)}`;
      const res = await fetch(url, { credentials: "include" });
      if (res.status === 401) {
        setUser(null);
        handleSignIn("pdf");
        return;
      }
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Download failed (${res.status}): ${txt.slice(0, 200)}`);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const safe = (current.title || "memo").replace(/[^a-zA-Z0-9_-]+/g, "_").slice(0, 60);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `BluePrintAI_${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
    } catch (e) {
      console.error("PDF download failed", e);
      setError(e.message || "PDF download failed");
    } finally {
      setDownloadingPdf(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/blueprint/showcase`).then((r) => {
      setShowcase(r.data);
      setCurrent(r.data);
    }).catch((e) => setError(e.message));
  }, []);

  const handleGenerate = async ({ idea, industry, target_market }) => {
    setLoading(true);
    setError(null);
    try {
      // 1. Kick off background job
      const start = await axios.post(`${API}/blueprint/generate`, { idea, industry, target_market });
      const jobId = start?.data?.job_id;
      if (!jobId) throw new Error("No job_id returned");

      // 2. Poll for completion (every 3s, up to 4 minutes)
      const maxAttempts = 80;
      let blueprintId = null;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise((res) => setTimeout(res, 3000));
        const js = await axios.get(`${API}/blueprint/job/${jobId}`);
        const s = js?.data?.status;
        if (s === "done") {
          blueprintId = js.data.blueprint_id;
          break;
        }
        if (s === "failed") {
          throw new Error(js.data.error || "Generation failed");
        }
      }
      if (!blueprintId) throw new Error("Generation timed out — please retry.");

      // 3. Fetch the saved blueprint
      const r = await axios.get(`${API}/blueprint/${blueprintId}`);
      if (!r?.data?.blueprint) throw new Error("Empty blueprint payload");

      setCurrent(r.data.blueprint);
      setModalOpen(false);
      setTimeout(() => memoRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
    } catch (e) {
      console.error("Blueprint generate failed", e);
      setError(e?.response?.data?.detail || e?.message || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const scrollToMemo = () => memoRef.current?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="App min-h-screen bg-paper text-[#0A0A0A]">
      <TopBar
        onGenerate={() => { setModalIdea(""); setModalOpen(true); }}
        onDownload={handleDownload}
        canDownload={!!current && !loading && inMemo}
        downloading={downloadingPdf}
        user={user}
        onSignIn={() => handleSignIn(null)}
        onSignOut={handleSignOut}
      />
      <MarqueeBanner />
      <TOC visible={inMemo && !loading} />

      <Hero
        onGenerate={() => { setModalIdea(""); setModalOpen(true); }}
        onScrollToMemo={scrollToMemo}
      />

      <EvaluatorTool
        onUpgradeClick={(prefillIdea) => {
          setModalIdea(prefillIdea || "");
          setModalOpen(true);
        }}
      />

      <div ref={memoRef}>
        {current === showcase && showcase && <FeaturedBadge />}

        <main className="max-w-7xl mx-auto px-6 sm:px-10 lg:pl-[260px] pt-16 sm:pt-24 pb-20">
          {error && (
            <div data-testid="error" className="border border-[#0A0A0A] bg-[#0A0A0A] text-[#F9F8F6] p-5 mb-10 font-mono text-xs">
              ERROR · {error}
            </div>
          )}

          {loading ? (
            <LoadingMemo />
          ) : current ? (
            <>
              <BlueprintView data={current} />
              <div className="mt-20 border-t border-[#0A0A0A]/15 pt-10 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] mb-1">End of memo</div>
                  <div className="font-serif text-xl text-[#0A0A0A]">Take it with you.</div>
                </div>
                <button
                  data-testid="bottom-download"
                  onClick={handleDownload}
                  disabled={downloadingPdf}
                  className="bg-[#0A0A0A] text-[#F9F8F6] font-mono text-xs tracking-[0.22em] uppercase px-6 py-4 flex items-center gap-3 hover:bg-[#002FA7] disabled:opacity-60"
                >
                  {downloadingPdf
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparing PDF…</>
                    : user
                      ? <><Download className="w-4 h-4" /> Download PDF</>
                      : <><Lock className="w-4 h-4" /> Sign in to download PDF</>}
                </button>
              </div>
            </>
          ) : (
            <div className="py-20 text-center text-[#5C5C5C] font-mono text-xs uppercase tracking-widest">Loading featured memo…</div>
          )}
        </main>
      </div>

      <Footer />

      <GenerateModal
        open={modalOpen}
        onClose={() => !loading && setModalOpen(false)}
        onSubmit={handleGenerate}
        loading={loading}
        initialIdea={modalIdea}
      />
      <SignInModal
        open={signInOpen}
        onClose={() => setSignInOpen(false)}
        reason={signInReason}
      />
    </div>
  );
}

export default App;
